import { useCallback, useEffect, useRef, useState } from 'react'

import {
  delayFetchApiKey,
  fetchApiKey,
  getApiKey,
  isApiKeyInvalid,
  isGuest as isPageGuest,
  isReportXpert,
  loadQuestionSuccess,
  loadQuestionsSuccess,
  renderExportProps
} from '@aytm/app/stats/lib/stat-page'
import { questionPositionToOldId } from '@aytm/app/stats/page/components/lib/utils'

import {
  calcBadData,
  calcJobEta,
  JOB_POLL_DELAY,
  readJobEnvelope,
  requestJobData
} from '@aytm/app/stats/page/question-views/components/data-run/lib/job'
import { questionOldIdToId } from '@aytm/app/stats/page/components/lib/utils'
import { resolveJobState } from '@aytm/app/stats/page/question-views/components/data-run/lib/utils'

// Drives the run-a-report job, ported from the coffee DataRunView: it starts the crunch, polls
// until the server stops saying "running", and hands the finished answer back to the page loader.
//
// The block that mounts it supplies what identifies the question and what to do with the fresh
// answer; everything else - the transport, the progress arithmetic, the retry on an invalid key -
// lives here.
export default function useDataRun({
  answer,
  type,
  questionKey,
  schemaKey,
  jobUrl,
  jobType,
  defaultJobStatus,
  calcBadData: calcBadDataProp,
  onBadData,
  onComplete
}) {
  const seeded = resolveJobState({ answer, isGuest: isPageGuest(), defaultJobStatus })

  const [job, setJob] = useState(seeded)
  const [isRunning, setIsRunning] = useState('running' === seeded.jobStatus)

  const timer = useRef(null)
  const request = useRef(null)
  const jobRef = useRef(job)
  jobRef.current = job
  const isRunningRef = useRef(isRunning)
  isRunningRef.current = isRunning

  // The answer usually lands after the block mounts, so the job follows it rather than keeping
  // the status it mounted with -- a question that arrives idle offers its run. A run this block
  // is polling owns the state until it stops.
  useEffect(() => {
    if (isRunningRef.current) return

    setJob(current =>
      current.jobStatus === seeded.jobStatus &&
      current.jobProgress === seeded.jobProgress &&
      current.jobEta === seeded.jobEta
        ? current
        : seeded
    )
  }, [seeded.jobStatus, seeded.jobProgress, seeded.jobEta])

  const inputs = useRef()
  inputs.current = { type, questionKey, schemaKey, jobUrl, jobType, calcBadDataProp, onBadData, onComplete }

  // the reply handler and the request that triggers it are mutually recursive; the ref keeps
  // each round trip on the current handler rather than the one this render captured
  const checkDataRef = useRef()

  const clearPending = useCallback(() => {
    if (timer.current) clearTimeout(timer.current)
    timer.current = null
    request.current?.abort?.()
    request.current = null
  }, [])

  useEffect(() => clearPending, [clearPending])

  const stop = useCallback(() => {
    clearPending()

    setJob(current => ({
      jobStatus: 'running' === current.jobStatus ? (isPageGuest() ? 'disabled' : 'idle') : current.jobStatus,
      jobProgress: 0,
      jobEta: 0
    }))
    setIsRunning(false)

    inputs.current.onComplete?.()
    renderExportProps(true)
  }, [clearPending])

  // One round trip. `envelope` carries the timestamp / job id the previous reply handed back, so
  // a poll resumes the same job instead of starting a new one.
  const fetchData = useCallback(
    (envelope = {}) => {
      const run = key => {
        const { questionKey, schemaKey, jobUrl, jobType } = inputs.current
        const oldQuestionKey = questionPositionToOldId(questionKey)

        request.current = requestJobData(
          {
            jobStatus: jobRef.current.jobStatus,
            key,
            questionKey: oldQuestionKey,
            schemaKey,
            type: jobType,
            jobUrl,
            response: envelope.response,
            timestamp: envelope.timestamp,
            queryHash: envelope.queryHash,
            jobId: envelope.jobId
          },
          { onSuccess: data => checkDataRef.current(data), onError: () => stop() }
        )
      }

      if (isApiKeyInvalid()) fetchApiKey({ forceUpdate: true, callback: run })
      else run(getApiKey())
    },
    [stop]
  )

  const poll = useCallback(
    (data, { immediate } = {}) => {
      if (immediate) return fetchData(data || {})

      timer.current = setTimeout(() => fetchData(data || {}), JOB_POLL_DELAY)
    },
    [fetchData]
  )

  const setProgress = useCallback(jobProgress => {
    setJob(current => ({
      jobStatus: 'running',
      jobProgress,
      jobEta: calcJobEta({ jobProgress, previousProgress: current.jobProgress, previousEta: current.jobEta })
    }))
  }, [])

  const checkData = useCallback(
    data => {
      clearPending()

      const { type, questionKey, schemaKey, calcBadDataProp, onBadData } = inputs.current

      const stats = data?.response?.stats || {}
      const id = questionOldIdToId(questionPositionToOldId(questionKey) || schemaKey)
      const questionAnswer = stats[id] || {}

      const badData = calcBadDataProp ? calcBadDataProp(questionAnswer) : calcBadData({ answer: questionAnswer, type })

      if ('OK' === data?.status) onBadData?.(badData)

      const result = readJobEnvelope({ data, answer: questionAnswer, badData, isXpert: isReportXpert() })

      if ('refresh-key' === result.action) {
        return delayFetchApiKey({ forceUpdate: true, status: 'invalid', callback: () => fetchData({}) })
      }

      if ('poll' === result.action) {
        setProgress(result.jobProgress)
        return poll(data, { immediate: result.immediate })
      }

      stop()

      if ('loaded' === result.action) {
        if (isReportXpert()) loadQuestionsSuccess(null, data)
        else loadQuestionSuccess(schemaKey, null, data, false)
      }
    },
    [clearPending, fetchData, poll, setProgress, stop]
  )

  checkDataRef.current = checkData

  // The button: start crunching, and keep the bar moving until the server says otherwise.
  const onRun = useCallback(() => {
    setIsRunning(true)
    setProgress(jobRef.current.jobProgress)
    fetchData({})
  }, [fetchData, setProgress])

  // A job already running when the block mounts is picked up rather than restarted.
  const started = useRef(false)
  useEffect(() => {
    if (started.current || 'running' !== seeded.jobStatus) return
    started.current = true

    onRun()
  }, [seeded.jobStatus, onRun])

  return { ...job, isRunning, onRun }
}
